<template>
  <div class="summary-tab">
    <div v-if="loading" class="summary-loading">
      <scale-loader color="#3d6b9a"></scale-loader>
    </div>

    <template v-else>
      <!-- Sub-navegacion: S01-S05 + Listas. El top se mide en runtime (bajo la barra de tabs). -->
      <div class="sub-nav" :style="{ top: subNavTop + 'px' }">
        <button v-for="s in subs" :key="s.id" class="sub-btn" :class="{ active: activeSub === s.id }"
                :disabled="!s.enabled" @click="activeSub = s.id">
          <b-icon pack="fas" :icon="s.icon" size="is-small"></b-icon>
          <span>{{ $t(s.label) }}</span>
          <span v-if="!s.enabled" class="soon">{{ $t('prm_summary_soon') }}</span>
        </button>

        <!-- Grupo derecho: exportar + moneda local + fuente de datos. -->
        <div class="nav-right">
          <button class="exp-btn" :disabled="exporting" v-tip="$t('prm_summary_export_hint')" @click="exportExcel">
            <b-icon pack="fas" :icon="exporting ? 'spinner' : 'file-excel'" :custom-class="exporting ? 'fa-spin' : ''" size="is-small"></b-icon>
            <span>{{ $t('prm_summary_export_button') }}</span>
          </button>

          <button class="sub-btn source" :class="{ active: sourceMode }" @click="sourceMode = !sourceMode">
            <b-icon pack="fas" icon="database" size="is-small"></b-icon>
            <span>{{ $t('prm_summary_source_button') }}</span>
          </button>
        </div>
      </div>

      <!-- Banner explicativo del modo "Fuente de datos" -->
      <div v-if="sourceMode" class="source-banner">
        <b-icon pack="fas" icon="circle-info" size="is-small"></b-icon>
        <span v-html="$t('prm_summary_source_hint')"></span>
      </div>

      <s01-summary v-if="activeSub === 's01'"
        :s01="doc.s01" :sources="sources" :key-dates="(doc.s02 || {}).keyDates || {}" :billing="billing" :risks="doc.s05 || []" :rag="rag" :mode="sourceMode" :reset-key="loadCount"
        @save-field="saveField" @save-list="saveList">
      </s01-summary>

      <s02-contract v-else-if="activeSub === 's02'"
        :doc="doc" :sources="sources" :project="project" :mode="sourceMode" :reset-key="loadCount"
        @save-field="saveField" @save-list="saveList"
        @files-changed="refreshContractFiles" @remove-file="removeContractFile">
      </s02-contract>

      <s03-finance v-else-if="activeSub === 's03'"
        :doc="doc" :sources="sources" :project="project" :billing="billing" :mode="sourceMode" :reset-key="loadCount"
        :active-set-id="activeSetId"
        @save-field="saveField" @save-list="saveList" @set-change="activeSetId = $event"
        @files-changed="refreshContractFiles" @remove-file="removeContractFile">
      </s03-finance>

      <s04-documentation v-else-if="activeSub === 's04'"
        :doc="doc" :sources="sources" :project="project" :mode="sourceMode" :reset-key="loadCount"
        @save-list="saveList" @files-changed="refreshContractFiles" @remove-file="removeContractFile">
      </s04-documentation>

      <s05-risks v-else-if="activeSub === 's05'"
        :risks="doc.s05" :sources="sources" :mode="sourceMode" :reset-key="loadCount"
        @save="(items) => saveList({ path: 's05', items })">
      </s05-risks>

      <s06-field v-else-if="activeSub === 's06'" :doc="doc" :sources="sources" :mode="sourceMode" :reset-key="loadCount"
        @save-field="saveField" @save-list="saveList"></s06-field>
    </template>
  </div>
</template>

<script>
  import axios from 'axios'
  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
  import S01Summary from './s01-summary.vue'
  import S02Contract from './s02-contract.vue'
  import S03Finance from './s03-finance.vue'
  import S04Documentation from './s04-documentation.vue'
  import S05Risks from './s05-risks.vue'
  import S06Field from './s06-field.vue'
  import { computeRag, paymentPlanTotals, penaltyTotal as calcPenaltyTotal } from './summary-model'
  import { getRate } from '../shared/currency'
  import { tip } from '../shared/tip'

  export default {
    name: 'PrmSummaryTab',

    components: { ScaleLoader, S01Summary, S02Contract, S03Finance, S04Documentation, S05Risks, S06Field },

    directives: { tip },

    props: {
      project: { type: Object, required: true },
      prmVersion: { type: Number, default: 0 }, // sube al importar el PRM → recargamos datos derivados
    },

    data: () => ({
      loading: true,
      exporting: false,
      activeSub: 's01',
      sourceMode: false, // modo "Fuente de datos": marca editables y muestra el origen del resto
      loadCount: 0,
      subNavTop: 200, // top de la sub-nav fija; se mide en runtime bajo la barra de tabs principales
      activeSetId: 'principal', // conjunto de hitos y finanzas visible en S03 (contrato principal o subcontrato)
      doc: { s01: {}, s05: [], meta: {} },
      sources: {},
      fxRate: null, // tasa de cambio (moneda local -> €) para valorar el importe del contrato
      today: new Date().toISOString().slice(0, 10), // para la facturación vencida
    }),

    computed: {
      base () {
        return `/api/v2/project/${this.project.id}/dashboard-prm/summary`
      },
      subs () {
        return [
          { id: 's01', label: 'prm_summary_sub_s01', icon: 'clipboard-list', enabled: true },
          { id: 's02', label: 'prm_summary_sub_s02', icon: 'file-lines', enabled: true },
          { id: 's03', label: 'prm_summary_sub_s03', icon: 'landmark', enabled: true },
          { id: 's04', label: 'prm_summary_sub_s04', icon: 'folder-open', enabled: true },
          { id: 's05', label: 'prm_summary_sub_s05', icon: 'shield-halved', enabled: true },
          { id: 's06', label: 'prm_summary_sub_s06', icon: 'screwdriver-wrench', enabled: true },
        ]
      },
      // Estado RAG del proyecto (S01): avance + margen + riesgo del registro S05.
      rag () {
        const p = this.sources.progress || {}
        const prm = this.sources.prm || {}
        return computeRag({
          progressReal: p.real,
          progressScheduled: p.scheduled,
          marginActual: prm.actualMargin,
          marginCommercial: prm.commercialMargin,
          risks: this.doc.s05,
        })
      },
      // Facturación de cliente (S01) derivada de la tabla de Facturación de S03: solo lectura, en vivo.
      billing () {
        const s03 = this.doc.s03 || {}
        // Importes en € = importe local x tipo de cambio manual (mismo que la tarjeta de S03).
        // La tasa automática del API queda desactivada de momento (ver loadFxRate).
        const rate = (Number.isFinite(Number(s03.exchangeRate)) && Number(s03.exchangeRate) > 0) ? Number(s03.exchangeRate) : 1
        const t = paymentPlanTotals(s03.paymentPlan, rate, s03.collectionDays, this.today)
        const contractEur = (Number(s03.contractAmountLocal) || 0) * rate
        // Penalidad total = min(Σ penalidades calculadas, importe total × CAP%), de los hitos penalizables de S03.
        const totalAmount = (this.sources.prm && this.sources.prm.totalAmount) || 0
        // Base de "pendiente de facturar": importe del contrato (€) si está puesto; si no, la venta total.
        const invoiceBase = (Number(s03.contractAmountLocal) || 0) !== 0 ? contractEur : totalAmount
        return {
          invoiced: t.invoiced,
          pendingToInvoice: invoiceBase - t.invoiced, // (importe del contrato € o venta total) - facturado
          collected: t.collected,
          invoicedNotCollected: t.notCollected,
          invoicedOverdue: t.overdue,
          penaltyTotal: calcPenaltyTotal(s03.penaltyMilestones, totalAmount, (this.doc.meta || {}).penaltyCapPct),
        }
      },
    },

    watch: {
      'project.id': { immediate: true, handler () { this.load() } },
      // Al importar el PRM o reabrir el Resumen: recarga en silencio para refrescar los datos
      // derivados del PRM (importes/márgenes/RAG) sin el parpadeo del spinner.
      prmVersion (v) { if (v > 0) this.load(true) },
    },

    methods: {
      async load (silent = false) {
        if (!silent) this.loading = true
        this.loadCount++
        try {
          const { data } = await axios.get(this.base)
          this.doc = data.data.doc
          this.sources = data.data.sources
          // Si el conjunto de hitos y finanzas seleccionado ya no existe (borrado), volvemos al principal.
          const sets = (this.doc.s03 && this.doc.s03.financeSets) || []
          if (this.activeSetId !== 'principal' && !sets.some((s) => s.id === this.activeSetId)) {
            this.activeSetId = 'principal'
          }
        } catch (e) {
          if (!silent) this.$notify.error((e && e.error_msg) || this.$t('error_retrieving_data'))
        } finally {
          if (!silent) this.loading = false
        }
      },

      // Tasa automática del API — DESACTIVADA de momento (usamos la manual de S03). Se
      // conserva por si se retoma: bastaría con llamarla en load() y usar fxRate en `billing`.
      async loadFxRate () {
        const cur = (this.doc.s03 && this.doc.s03.contractLocalCurrency) || 'USD'
        try {
          const { rate } = await getRate(cur, 'EUR')
          this.fxRate = rate
        } catch (e) {
          this.fxRate = null
        }
      },

      // Genera y descarga el Resumen ejecutivo en Excel (6 hojas, foto de los datos).
      async exportExcel () {
        if (this.exporting) return
        this.exporting = true
        try {
          const response = await axios.get(`${this.base}/export`, { responseType: 'blob' })
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.download = `Resumen ejecutivo - ${this.project.name || 'export'}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } catch (e) {
          this.$notify.error(this.$t('error_retrieving_data'))
        } finally {
          this.exporting = false
        }
      },

      // Autosave de un campo escalar: actualiza el estado local (para recalcular el RAG) y persiste.
      saveField ({ path, value }) {
        this.setByPath(path, value)
        axios.put(`${this.base}/field`, { path, value })
          .catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      // Guarda una lista completa (alta/baja/edición de filas).
      saveList ({ path, items }) {
        this.setByPath(path, items)
        axios.put(`${this.base}/list`, { path, items })
          .catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      // Refresca el mapa de ficheros PDF de avales/seguros tras una subida (mantiene el resto del estado).
      async refreshContractFiles () {
        try {
          const { data } = await axios.get(`/api/v2/project/${this.project.id}/dashboard-prm/contract-files`)
          this.sources.contractFiles = data.data
        } catch (e) { /* silencioso: el usuario puede reintentar */ }
      },

      // Borra del bucket el PDF de una fila eliminada y refresca el mapa.
      async removeContractFile (fileId) {
        try {
          await axios.delete(`/api/v2/project/${this.project.id}/dashboard-prm/contract-files/${fileId}`)
          this.refreshContractFiles()
        } catch (e) {
          this.$notify.error(this.$t('error_saving_data'))
        }
      },

      // Fija un valor en doc por ruta ('s01.client', 's05'...), de forma reactiva.
      setByPath (path, value) {
        const parts = path.split('.')
        let node = this.doc
        for (let i = 0; i < parts.length - 1; i++) {
          if (node[parts[i]] == null) this.$set(node, parts[i], {})
          node = node[parts[i]]
        }
        this.$set(node, parts[parts.length - 1], value)
      },

      // La sub-nav se fija justo bajo la barra de tabs principales (sticky a 161px): su top es
      // 161 + el alto real de la barra, medido en runtime. Asi queda pegada sin salto ni holgura,
      // sea cual sea su alto renderizado (el 161 es el mismo offset fijado en dashboard-prm.vue).
      measureSubNav () {
        const tabs = document.querySelector('.prm-tabs .tabs')
        if (tabs) this.subNavTop = 161 + Math.round(tabs.offsetHeight)
      },
    },

    mounted () {
      this.$nextTick(this.measureSubNav)
      window.addEventListener('resize', this.measureSubNav)
    },

    beforeDestroy () {
      window.removeEventListener('resize', this.measureSubNav)
    },
  }
</script>

<style lang="scss" scoped>
  .summary-tab { min-width: 0; }
  .summary-loading { display: flex; justify-content: center; padding: 60px 0; }

  // Sub-navegacion fija bajo los tabs principales (sticky a 161px + su alto), sin salto ni holgura:
  // - margin-top negativo cancela el margen inferior de los tabs (14px) para que quede pegada y
  //   no "viaje" hacia arriba al fijarse.
  // - z-index por debajo de los tabs (8) + un riser blanco hacia arriba (box-shadow, sin blur):
  //   tapa cualquier holgura y deja que los tabs la cubran en el solape, para que el contenido
  //   nunca se vea por detras al desplazar (robusto ante 1-2px de diferencia en el alto de los tabs).
  // El top se fija por :style inline (medido en runtime); el resto define el pegado sin holgura.
  .sub-nav {
    position: sticky; z-index: 7; background: #fff;
    margin: -14px 0 8px; padding: 10px 0 6px;
    box-shadow: 0 -18px 0 0 #fff;
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  }
  .sub-btn {
    display: inline-flex; align-items: center; gap: 6px; border: 1px solid #c3ccd8; background: #fff;
    color: #3d5570; font-size: 11px; font-weight: 600; padding: 5px 10px; border-radius: 6px; cursor: pointer;
  }
  .sub-btn:hover:not(:disabled) { background: #f6f9fc; }
  .sub-btn.active { background: #1e3046; color: #fff; border-color: #1e3046; }
  // Hover del tab activo: mantiene el fondo oscuro para que el texto blanco siga legible.
  .sub-btn.active:hover:not(:disabled) { background: #2a3f5a; }
  .sub-btn:disabled { opacity: .55; cursor: not-allowed; }

  // Grupo derecho (export + moneda + fuente de datos).
  .nav-right { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; }

  // Botón exportar a Excel (verde, familia del resto de exports).
  .exp-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid #a9d0b4; background: #eef7f0; color: #1e6b3d;
    font-size: 11px; font-weight: 600; padding: 5px 10px; border-radius: 6px; cursor: pointer;
  }
  .exp-btn:hover:not(:disabled) { background: #e2efe7; }
  .exp-btn:disabled { opacity: .6; cursor: not-allowed; }

  .sub-btn.source { color: #8a6d00; border-color: #e0b52a; }
  .sub-btn.source.active { background: #fff4d6; color: #8a6d00; border-color: #e0b52a; }
  .sub-btn.source.active:hover:not(:disabled) { background: #fbecc0; }
  .soon { font-size: 9px; text-transform: uppercase; color: #9aa4b2; }

  // El texto del banner puede llevar <b> (v-html); mantenemos el color heredado.
  .source-banner ::v-deep b { font-weight: 700; }

  .source-banner {
    display: flex; align-items: flex-start; gap: 8px; margin-bottom: 10px;
    border: 1px solid #e0b52a; background: #fff4d6; color: #8a6d00;
    border-radius: 6px; padding: 8px 12px; font-size: 11px; line-height: 1.45;
  }
</style>
